import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { mcpRestClient } from '@/lib/mcp/restClient';
import { useOrganizationsStore } from '@/stores/organizations';
import { toUserMessage } from '@/lib/api/errors';
import type {
  VirtualServer,
  VirtualServerCreate,
  VirtualServerUpdate,
  ToolMapping,
} from '@/lib/mcp/types';

export const useMcpVServersStore = defineStore('mcpVServers', () => {
  // State
  const virtualServers = ref<VirtualServer[]>([]);
  const selectedVServerId = ref<string | null>(null);
  const toolMappings = ref<Record<string, ToolMapping[]>>({});
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeServers = computed(() =>
    virtualServers.value.filter((s) => s.is_active),
  );

  const inactiveServers = computed(() =>
    virtualServers.value.filter((s) => !s.is_active),
  );

  const selectedVServer = computed(() => {
    if (!selectedVServerId.value) return null;
    return virtualServers.value.find((s) => s.id === selectedVServerId.value) ?? null;
  });

  // Actions
  const fetchVServers = async (orgId: string): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      virtualServers.value = await mcpRestClient.listVServers(orgId);
    } catch (e) {
      error.value = toUserMessage(e, 'Failed to load virtual servers');
    } finally {
      isLoading.value = false;
    }
  };

  const createVServer = async (
    orgId: string,
    payload: VirtualServerCreate,
  ): Promise<VirtualServer> => {
    // The MCP endpoint /{org_slug}/{vserver_slug}/mcp resolves via keys that
    // are only written when the slug is stored as "{org_slug}/{vserver_slug}",
    // and a server with no capabilities exposes nothing whatever its mappings
    // say. Normalise both here so no caller can create an unreachable server.
    const orgSlug = useOrganizationsStore().currentOrganization?.slug ?? orgId;
    const normalized: VirtualServerCreate = {
      ...payload,
      slug: payload.slug.includes('/') ? payload.slug : `${orgSlug}/${payload.slug}`,
      capabilities: payload.capabilities ?? { tools: true },
    };
    const created = await mcpRestClient.createVServer(orgId, normalized);
    virtualServers.value = [...virtualServers.value, created];
    return created;
  };

  const updateVServer = async (
    orgId: string,
    vserverId: string,
    payload: VirtualServerUpdate,
  ): Promise<VirtualServer> => {
    const updated = await mcpRestClient.updateVServer(orgId, vserverId, payload);
    virtualServers.value = virtualServers.value.map((s) =>
      s.id === vserverId ? updated : s,
    );
    return updated;
  };

  const deleteVServer = async (orgId: string, vserverId: string): Promise<void> => {
    await mcpRestClient.deleteVServer(orgId, vserverId);
    virtualServers.value = virtualServers.value.filter((s) => s.id !== vserverId);
    if (selectedVServerId.value === vserverId) {
      selectedVServerId.value = null;
    }
  };

  const fetchToolMappings = async (
    orgId: string,
    vserverId: string,
  ): Promise<ToolMapping[]> => {
    const mappings = await mcpRestClient.listToolMappings(orgId, vserverId);
    toolMappings.value = { ...toolMappings.value, [vserverId]: mappings };
    return mappings;
  };

  const setToolMappings = async (
    orgId: string,
    vserverId: string,
    mappings: ToolMapping[],
  ): Promise<void> => {
    await mcpRestClient.setToolMappings(orgId, vserverId, mappings);
    toolMappings.value = { ...toolMappings.value, [vserverId]: mappings };
  };

  const setSelectedVServer = (id: string | null): void => {
    selectedVServerId.value = id;
  };

  return {
    // State
    virtualServers,
    selectedVServerId,
    toolMappings,
    isLoading,
    error,
    // Getters
    activeServers,
    inactiveServers,
    selectedVServer,
    // Actions
    fetchVServers,
    createVServer,
    updateVServer,
    deleteVServer,
    fetchToolMappings,
    setToolMappings,
    setSelectedVServer,
  };
});
